import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Card,
  Grid,
  TextField,
  useMediaQuery,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { InfoOutlined, Search } from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  getAllRewardsAction,
  getNetworkInfluencerRewardsAction,
} from '../APIs/action';
import { toast, ToastContainer } from 'react-toastify';
import GiftTabs from './GiftTab';
import { GiftStatusEnum, REWARD_TABS } from '../utils';
import { useDispatch, useSelector } from 'react-redux';
import GiftTableView from './GiftTableView';
import GiftMobileCards from './GiftMobileCards';
import NetworkInfluencerTable from './NetworkInfluencerTable';
import NetworkInfluencerCard from './NetworkInfluencerCard';
import { useCleverTapPageTracking } from '../../../../utils/clevertap';
import Entity from '../../../../v3/Common/Entity';
import StickyEntity from '../../../../v3/Common/Entity/StickyEntity';

const RewardsTabs = ({
  allowedTabs = [],
  networkList = false,
  network_id = null,
  selectedStartDate = null,
  beneficiary_esi_id = null,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // CleverTap screen tracking
  useCleverTapPageTracking('Rewards_Tabs', 'rewards');
  const [selectedDate, setSelectedDate] = useState(selectedStartDate || null);

  const selectedTabReward = useSelector(
    (state) => state.rewardsReducer.selectedTabReward,
  );
  const [selectedTab, setSelectedTab] = useState(
    selectedTabReward && allowedTabs.includes(selectedTabReward)
      ? selectedTabReward
      : allowedTabs[0],
  );
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rewardData, setrewardData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Get manufacturer and entity from Redux via Entity component
  const { selectedSupplierData, selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer
  );
   
  useEffect(() => {
    if (allowedTabs.length > 0) {
      const newTab =
        selectedTab === REWARD_TABS['NETWORK_INFLUENCER']
          ? 'NETWORK INFLUENCER'
          : selectedTab;
      dispatch({
        type: 'SET_SELECTED_TAB_REWARD',
        payload: allowedTabs.includes(newTab) ? newTab : allowedTabs[0],
      });
    }
  }, [selectedTab, allowedTabs]);
  const fetchData = async (startDate, endDate, selectedManufacturer, selectedCompanyEntity) => {
    const payload = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      manufacturer_id: selectedManufacturer, 
      
      ...(!networkList &&
      network_id &&
      selectedTab === REWARD_TABS['NETWORK_INFLUENCER']
        ? { network_id: network_id, network_esi_id: beneficiary_esi_id, user_esi_id: selectedCompanyEntity,  }
        : {
            type:
              selectedTab === REWARD_TABS['NETWORK_INFLUENCER']
                ? 'NETWORK_INFLUENCER'
                : selectedTab,
            user_esi_id: selectedCompanyEntity,
          }),
    };
    setLoading(true);
    let response;
    if (
      !networkList &&
      network_id &&
      REWARD_TABS['NETWORK_INFLUENCER'] === selectedTab
    ) {
      response = await getNetworkInfluencerRewardsAction(payload);
    } else {
      response = await getAllRewardsAction(payload);
    }

    const { data, message, error } = response;
    setLoading(false);
    
    if (!error) {
      setrewardData(data);
    } else {
      toast.error(message);
    }
  };
  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  const handleShowDetails = (gift_id) => {
    navigate('/benefits/reward-detail', {
      state: {
        gift_id: gift_id,
        selectedCompanyEntity: selectedCompanyEntity,
        type:
          selectedTab === REWARD_TABS['NETWORK_INFLUENCER']
            ? 'NETWORK_INFLUENCER'
            : selectedTab,
        selectedManufacturer: selectedManufacturer,
      },
    });
  };
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    const twoMonthAgo = dayjs().subtract(2, 'month');
    setStartDate(twoMonthAgo);
    setEndDate(dayjs());
  }, []);

  const { network_gifts_data, gifts_summary_data, gift_list } =
    rewardData || {};
  const allGifts = gift_list ? gift_list.flatMap((item) => item.data) : [];

  const filteredData = allGifts.filter((gift) => {
    return (
      (selectedStatus === 0 || gift.status === selectedStatus) &&
      (gift.beneficiary_name
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        gift.beneficiary_phone.includes(searchQuery))
    );
  });
  
  const filteredNetworkList = rewardData?.network_list?.filter((item) => {
    return (
      item.beneficiary_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.beneficiary_phone.includes(searchQuery)
    );
  });
  
  useEffect(() => {
    if (gift_list) {
      setSelectedStatus(gift_list[0]?.id);
    }
  }, [gift_list]);

  const [duration, setDuration] = useState('1');

  useEffect(() => {
    if (selectedDate) {
      const start = dayjs(selectedDate);
      const end = dayjs();
      setStartDate(start);
      setEndDate(end);
      const monthsDiff = end.diff(start, 'month');
      setDuration(String(monthsDiff));
      setSelectedDate(null);
    }
  }, [selectedDate]);
  
  useEffect(() => {
    if (!selectedDate) {
      const today = dayjs();
      const months = parseInt(duration, 10);
      const start = today.subtract(months, 'month');
      setStartDate(start);
      setEndDate(today);
    }
  }, [duration, selectedDate]);

  useEffect(() => {
    if (startDate && endDate && selectedTab) {
      // Only fetch if we have manufacturer selected, and entity if required
      if (selectedManufacturer && (selectedCompanyEntity || selectedTab === REWARD_TABS['NETWORK_INFLUENCER'])) {
        fetchData(startDate, endDate, selectedManufacturer, selectedCompanyEntity);
      }
    }
  }, [startDate?.valueOf(), endDate?.valueOf(), selectedTab, selectedManufacturer, selectedCompanyEntity]);
  const isEntityEnabled = selectedSupplierData?.entity_enabled;
  return (
    <>
      <StickyEntity />
      <Box sx={{ mt: {xs : isEntityEnabled ? '96px': 0, md: 0 }, p: 2 }}>
        <ToastContainer />
        {rewardData?.sender_details && (
          <Grid
            item
            xs={12}
            sx={{ mb: 2 }}
          >
            <Card
              sx={{
                borderRadius: '12px',
                boxShadow:
                  '0 0 2px 0 rgba(145, 158, 171, 0.5), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
                p: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{ mb: 3, textTransform: 'Uppercase' }}
                  gutterBottom
                >
                  {rewardData?.sender_details?.role} Details
                </Typography>
              </Box>
              <Grid
                container
                spacing={4}
              >
                <Grid
                  item
                  xs={12}
                  sm={8}
                >
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={6}
                      sm={6}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        sx={{ color: '#636363', fontSize: '12px' }}
                      >
                        Name
                      </Typography>
                      <TextField
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                          },
                        }}
                        fullWidth
                        margin='dense'
                        size='small'
                        value={rewardData?.sender_details.name}
                        disabled
                      />
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={6}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        sx={{ color: '#636363', fontSize: '12px' }}
                      >
                        Phone Number
                      </Typography>
                      <TextField
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                          },
                        }}
                        fullWidth
                        margin='dense'
                        size='small'
                        value={rewardData?.sender_details.phone}
                        disabled
                      />
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={6}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        sx={{ color: '#636363', fontSize: '12px' }}
                      >
                        State
                      </Typography>
                      <TextField
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                          },
                        }}
                        fullWidth
                        margin='dense'
                        size='small'
                        value={rewardData?.sender_details.state}
                        disabled
                      />
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sm={6}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        sx={{ color: '#636363', fontSize: '12px' }}
                      >
                        District
                      </Typography>
                      <TextField
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                          },
                        }}
                        fullWidth
                        margin='dense'
                        size='small'
                        value={rewardData?.sender_details.district}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={4}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #c5c5c5',
                      borderRadius: '10px',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Typography sx={{ mb: 2 }}>
                      Current Month Business Volume
                    </Typography>
                    <Typography
                      variant='h6'
                      fontWeight='bold'
                      sx={{ color: 'orange', fontSize: '30px' }}
                    >
                      {rewardData?.sender_details
                        ?.current_month_business_volume || '-'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}
        {!network_id && (
          <Box sx={{ mb: 3, overflowX: 'auto' }}>
            <Tabs
              value={selectedTab}
              onChange={handleChange}
              sx={{ flexWrap: 'wrap' }}
            >
              {allowedTabs.length > 0 ? (
                allowedTabs.map((tab) => (
                  <Tab
                    key={tab}
                    label={REWARD_TABS[tab] || tab}
                    value={tab}
                    sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                  />
                ))
              ) : (
                <p>No tabs available</p>
              )}
            </Tabs>
          </Box>
        )}
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <Card
              sx={{
                borderRadius: '12px',
                boxShadow:
                  '0 0 2px 0 rgba(145, 158, 171, 0.5), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
                mb: 1,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography
                sx={{ color: '#636363', fontWeight: 'bold', minWidth: '60px' }}
              >
                Period
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Grid item>
                  {!isMobile ? (
                    <Tabs
                      value={duration}
                      onChange={(e, val) => setDuration(val)}
                      sx={{ minHeight: 36 }}
                    >
                      <Tab
                        label='1 Month'
                        value='1'
                      />
                      <Tab
                        label='3 Months'
                        value='3'
                      />
                      <Tab
                        label='12 Months'
                        value='12'
                      />
                    </Tabs>
                  ) : (
                    <Select
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      size='small'
                      sx={{ borderRadius: '10px', minWidth: 120 }}
                    >
                      <MenuItem value='1'>1 Month</MenuItem>
                      <MenuItem value='3'>3 Months</MenuItem>
                      <MenuItem value='12'>12 Months</MenuItem>
                    </Select>
                  )}
                </Grid>
              </Box>
            </Card>
          </Grid>
          {REWARD_TABS['NETWORK_INFLUENCER'] !== selectedTab && (
            <Grid
              item
              xs={12}
            >
              <Card
                sx={{
                  borderRadius: '12px',
                  boxShadow:
                    '0 0 2px 0 rgba(145, 158, 171, 0.5), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
                  mb: 2,
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant='h6'
                    sx={{ pb: 2, fontWeight: 'bold' }}
                  >
                    Summary
                  </Typography>

                  {/* Network Gifts */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: gifts_summary_data
                        ? '2px solid #A3A3A380'
                        : 'none',
                      pb: 3,
                      mb: 3,
                    }}
                  >
                    {network_gifts_data?.map((item, index) => (
                      <Box
                        key={item.display_name}
                        sx={{
                          borderRight:
                            index !== network_gifts_data.length - 1
                              ? '2px solid #A3A3A380'
                              : 'none',
                          px: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '50%',
                          textAlign: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant='subtitle1'
                            sx={{
                              fontSize: '14px',
                              color: '#9B9B9B',
                              fontWeight: 'bold',
                            }}
                          >
                            {item.display_name}
                          </Typography>
                          {item.display_name?.startsWith('Dormant') ? (
                            <Tooltip
                              title='No sales in the last 2 months.'
                              arrow
                              placement='top'
                            >
                              <InfoOutlined
                                fontSize='small'
                                sx={{
                                  color: '#9B9B9B',
                                  cursor: 'pointer',
                                  ml: 1,
                                }}
                              />
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            color: item.color,
                            fontSize: { xs: '1rem', md: '1.5rem' },
                            fontWeight: 'bold',
                          }}
                        >
                          {item.value ?? '-'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Gifts Summary */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {gifts_summary_data?.map((item, index) => (
                      <Box
                        key={item.display_name}
                        sx={{
                          borderRight:
                            index !== gifts_summary_data.length - 1
                              ? '2px solid #A3A3A380'
                              : 'none',
                          px: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '50%',
                          textAlign: 'center',
                        }}
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{
                            fontSize: '14px',
                            color: '#9B9B9B',
                            fontWeight: 'bold',
                          }}
                        >
                          {item.display_name}
                        </Typography>
                        <Typography
                          variant='subtitle2'
                          sx={{
                            color: item.color,
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {item.value !== undefined && item.value !== null
                            ? item.value
                            : '-'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Card>
            </Grid>
          )}
        </Grid>
        {loading ? (
          <Box
            sx={{
              height: '80vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {gift_list && (
              <GiftTabs
                giftListData={gift_list}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
              />
            )}

            {selectedTab !== REWARD_TABS['SELF'] && (
              <Grid
                container
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Grid
                  item
                  xs={12}
                >
                  <Card
                    sx={{
                      borderRadius: '12px',
                      boxShadow:
                        '0 0 2px 0 rgba(145, 158, 171, 0.5), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
                      p: 2,
                    }}
                  >
                    <Typography
                      sx={{ color: '#636363', fontWeight: 'bold', pb: 1 }}
                    >
                      Search By
                    </Typography>
                    <TextField
                      label='Beneficiary Name / Phone'
                      variant='outlined'
                      value={searchQuery}
                      onChange={handleSearchChange}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <IconButton>
                            <Search />
                          </IconButton>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '15px',
                        },
                      }}
                    />
                  </Card>
                </Grid>
              </Grid>
            )}
            {!isMobile ? (
              networkList && selectedTab === REWARD_TABS['NETWORK_INFLUENCER'] ? (
                <NetworkInfluencerTable
                  networkList={filteredNetworkList}
                  selectedStatus={selectedStatus}
                  handleShowDetails={handleShowDetails}
                  selectedStartDate={startDate ? startDate.format('YYYY-MM-DD') : null}
                />
              ) : (
                <GiftTableView
                  selectedTab={selectedTab}
                  filteredData={filteredData}
                  selectedStatus={selectedStatus}
                  handleShowDetails={handleShowDetails}
                  GiftStatusEnum={GiftStatusEnum}
                />
              )
            ) : networkList &&
              selectedTab === REWARD_TABS['NETWORK_INFLUENCER'] ? (
              <NetworkInfluencerCard
                networkList={filteredNetworkList}
                selectedStatus={selectedStatus}
                handleShowDetails={handleShowDetails}
                selectedStartDate={startDate ? startDate.format('YYYY-MM-DD') : null}
              />
            ) : (
              <GiftMobileCards
                selectedTab={selectedTab}
                filteredData={filteredData}
                selectedStatus={selectedStatus}
                handleShowDetails={handleShowDetails}
                GiftStatusEnum={GiftStatusEnum}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default RewardsTabs;
